import { useState, useEffect } from "react";
import {
  Paper,
  Stack,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Box,
  Checkbox,
  CircularProgress,
  Chip,
  Divider,
  FormControlLabel,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import getApiData from "../../../../../hooks/apiRequests/getRequest";
import postApiData from "../../../../../hooks/apiRequests/postsRequest";
import { useToast } from "../../../../shared/toastMessages/toastProvider";

const EmailList = ({ theme, trader, handleCloseModal, onEmailStateChange }) => {
  const [emailList, setEmailList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noResults, setNoResults] = useState(false);
  const [emptyReason, setEmptyReason] = useState(""); // "" | "none" | "undeliverable"
  const [mailingList, setMailingList] = useState([]);
  const [selectedMailingListIds, setSelectedMailingListIds] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [error, setError] = useState("");
  const [rejectedListIds, setRejectedListIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const { showToast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setNoResults(false);
      setEmptyReason("");
      setEmailList([]);
      setSelectedEmails([]);
      setError("");
      setRejectedListIds([]);

      // Emails were already discovered + validated in the Commodity search.
      const prefetched = Array.isArray(trader?.emails) ? trader.emails : [];
      if (prefetched.length > 0) {
        setEmailList(prefetched);
        setNoResults(false);
        setEmptyReason("");
        if (onEmailStateChange && trader?.emailState) {
          onEmailStateChange(trader.companyName, trader.emailState);
        }
        setLoading(false);
        return;
      }

      try {
        const response = await postApiData(
          "/Email/Search",
          {
            companyName: trader.companyName,
            postcode: trader.postcode,
            domain: trader.domain || "",
          },
          // Discovery + bulk deliverability can take a while.
          { timeout: 0 },
        );

        if (response?.error) {
          throw new Error(response?.data?.message || "Email search failed");
        }

        const payload = response.data || {};
        const emails = Array.isArray(payload)
          ? payload
          : Array.isArray(payload.emails)
            ? payload.emails
            : [];
        const emailState = payload.email_state || "";
        const message = payload.message || "";
        const undeliverable =
          emailState === "undeliverable" ||
          /emails found but are not deliverable/i.test(message) ||
          /emails found but not deliverable/i.test(message);

        if (onEmailStateChange) {
          if (emailState) {
            onEmailStateChange(trader.companyName, emailState);
          } else if (emails.length > 0) {
            onEmailStateChange(trader.companyName, "");
          } else if (undeliverable) {
            onEmailStateChange(trader.companyName, "undeliverable");
          } else {
            onEmailStateChange(trader.companyName, "no_emails_found");
          }
        }

        if (emails.length === 0) {
          setNoResults(true);
          setEmptyReason(undeliverable ? "undeliverable" : "none");
          setEmailList([]);
        } else {
          setEmailList(emails);
          setNoResults(false);
          setEmptyReason("");
        }
      } catch (error) {
        console.error(error);
        showToast("Failed to load emails.", "error");
        setNoResults(true);
        setEmptyReason("none");
        setEmailList([]);
      } finally {
        setLoading(false);
      }
    };

    if (!trader) return;
    fetchData();
  }, [trader, showToast, onEmailStateChange]);

  useEffect(() => {
    const fetchLists = async () => {
      try {
        const res = await getApiData("/Emailling/Create/List");

        const mapped = (res?.data || []).map((list) => ({
          id: list.id,
          label: list.item,
        }));

        setMailingList(mapped);
      } catch (err) {
        console.error(err);
        showToast("Failed to load mailing lists.", "error");
      }
    };

    fetchLists();
  }, [showToast]);

  const toggleEmail = (email) => {
    setSelectedEmails((prev) =>
      prev.includes(email)
        ? prev.filter((e) => e !== email)
        : [...prev, email]
    );
  };

  const allSelected =
    emailList.length > 0 && selectedEmails.length === emailList.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedEmails([]);
    } else {
      setSelectedEmails(emailList.map((item) => item.email));
    }
  };

  const handleSubmit = async () => {
    if (selectedMailingListIds.length === 0) {
      showToast("Please select at least one mailing list.", "error");
      return;
    }

    if (selectedEmails.length === 0) {
      showToast("Please select at least one email.", "error");
      return;
    }

    const payload = emailList
      .filter((item) => selectedEmails.includes(item.email))
      .map((item) => ({
        id: item.id ?? 0,
        name: item.name ?? "",
        email: item.email,
        maling_list: selectedMailingListIds,
        company_name: trader.companyName,
        job_role: item.role,
        time_added: new Date().toISOString(),
      }));

    try {
      setError("");
      setRejectedListIds([]);
      setSubmitting(true);

      const response = await postApiData("/Create/CreateList", payload);
      const body = response?.data || {};
      const rejected =
        Array.isArray(body.rejected_lists) ? body.rejected_lists : [];

      if (!response || response?.error || response?.status >= 400) {
        const rejectedIds = rejected.map((list) => list.id).filter(Boolean);
        const rejectedNames = rejected
          .map((list) => list.name)
          .filter(Boolean);
        const message =
          body.message ||
          (rejectedNames.length
            ? `Email already exists on: ${rejectedNames.join(", ")}`
            : "One or more of these emails are already on the selected mailing list.");

        setRejectedListIds(rejectedIds);
        setError(message);
        showToast(message, "error");
        return;
      }

      setSelectedEmails([]);
      setRejectedListIds([]);
      showToast("Emails added to the mailing list.", "success");
      if (onEmailStateChange) {
        onEmailStateChange(trader.companyName, "added");
      }
      handleCloseModal();
    } catch (err) {
      console.error(err);

      const body = err?.response?.data || {};
      const rejected = Array.isArray(body.rejected_lists)
        ? body.rejected_lists
        : [];
      const message =
        body.message ||
        "One or more of these emails are already on the selected mailing list.";

      setRejectedListIds(rejected.map((list) => list.id).filter(Boolean));
      setError(message);
      showToast(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    !loading &&
    !submitting &&
    emailList.length > 0 &&
    selectedMailingListIds.length > 0 &&
    selectedEmails.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: "12px",
          border: "1px solid #eee",
          bgcolor: "#fafafa",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: "#fff",
              border: "1px solid #eee",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <BusinessOutlinedIcon fontSize="small" color="action" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {trader.companyName}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              {trader.postcode && trader.postcode !== "N/A" ? (
                <Chip
                  size="small"
                  label={trader.postcode}
                  sx={{ borderRadius: "8px", bgcolor: "#fff", fontWeight: 500 }}
                />
              ) : null}
              {trader.domain ? (
                <Chip
                  size="small"
                  label={trader.domain}
                  sx={{ borderRadius: "8px", bgcolor: "#fff", fontWeight: 500 }}
                />
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
          <GroupsOutlinedIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Mailing lists
          </Typography>
        </Stack>

        <Autocomplete
          multiple
          options={mailingList}
          value={mailingList.filter((m) =>
            selectedMailingListIds.includes(m.id)
          )}
          onChange={(e, newValue) => {
            setSelectedMailingListIds(newValue.map((item) => item.id));
            if (error) setError("");
            if (rejectedListIds.length) setRejectedListIds([]);
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          filterSelectedOptions
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => {
              const rejected = rejectedListIds.includes(option.id);
              return (
                <Chip
                  key={option.id}
                  label={option.label}
                  size="small"
                  color={rejected ? "error" : "default"}
                  variant={rejected ? "filled" : "outlined"}
                  {...getTagProps({ index })}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: 500,
                    ...(rejected
                      ? {
                          bgcolor: "#ffebee",
                          color: "#c62828",
                          borderColor: "#ef9a9a",
                        }
                      : {}),
                  }}
                />
              );
            })
          }
          renderOption={(props, option) => {
            const rejected = rejectedListIds.includes(option.id);
            return (
              <li
                {...props}
                key={option.id}
                style={{
                  ...props.style,
                  color: rejected ? "#c62828" : props.style?.color,
                  fontWeight: rejected ? 600 : props.style?.fontWeight,
                }}
              >
                {option.label}
                {rejected ? " (already added)" : ""}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select mailing lists"
              placeholder={selectedMailingListIds.length ? "" : "Start typing..."}
              size="small"
              error={Boolean(error)}
              helperText={
                error
                  ? error
                  : "Choose one or more lists to add the selected emails to."
              }
            />
          )}
        />
      </Box>

      <Divider />

      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.25 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <MailOutlineIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Company emails
            </Typography>
          </Stack>

          {!loading && emailList.length > 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {selectedEmails.length} of {emailList.length} selected
            </Typography>
          ) : null}
        </Stack>

        {!loading && emailList.length > 0 ? (
          <FormControlLabel
            sx={{ mb: 1, ml: 0.25 }}
            control={
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={
                  selectedEmails.length > 0 &&
                  selectedEmails.length < emailList.length
                }
                onChange={toggleSelectAll}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Select all
              </Typography>
            }
          />
        ) : null}

        <Box
          sx={{
            border: "1px solid #eee",
            borderRadius: "12px",
            overflow: "hidden",
            bgcolor: "#fff",
            ...(emailList.length > 5 && !loading && !noResults
              ? { maxHeight: 300, overflowY: "auto" }
              : {}),
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                gap: 1.5,
              }}
            >
              <CircularProgress color="primary" size={36} />
              <Typography variant="body2" color="text.secondary">
                Searching emails for {trader.companyName}...
              </Typography>
            </Box>
          ) : noResults ? (
            <Box sx={{ px: 3, py: 5, textAlign: "center" }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: emptyReason === "undeliverable" ? "#fff8e1" : "#fafafa",
                  border: "1px solid #eee",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1.5,
                }}
              >
                <MailOutlineIcon
                  sx={{
                    color:
                      emptyReason === "undeliverable"
                        ? "#f57f17"
                        : "text.secondary",
                  }}
                />
              </Box>
              {emptyReason === "undeliverable" ? (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Emails found but are not deliverable
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    We found email addresses for{" "}
                    <strong>{trader.companyName}</strong>
                    {trader.postcode && trader.postcode !== "N/A"
                      ? ` (${trader.postcode})`
                      : ""}
                    , but they are not deliverable.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try another company, or add a different contact manually from the
                    mailing list editor.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    No emails found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    We couldn&apos;t find any emails for{" "}
                    <strong>{trader.companyName}</strong>
                    {trader.postcode && trader.postcode !== "N/A"
                      ? ` (${trader.postcode})`
                      : ""}
                    .
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try another company, or add contacts manually from the mailing list editor.
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            emailList.map((item, index) => {
              const checked = selectedEmails.includes(item.email);

              return (
                <Box
                  key={item.email}
                  onClick={() => toggleEmail(item.email)}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    borderBottom:
                      index === emailList.length - 1 ? "none" : "1px solid #f0f0f0",
                    bgcolor: checked ? "rgba(255,0,0,0.03)" : "transparent",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      bgcolor: checked ? "rgba(255,0,0,0.05)" : "#fafafa",
                    },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={() => toggleEmail(item.email)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.email}
                    </Typography>
                    {item.role || item.name ? (
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                        <PersonOutlineIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">
                          {[item.name, item.role].filter(Boolean).join(" · ")}
                        </Typography>
                      </Stack>
                    ) : null}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {!loading && emailList.length > 0 ? (
        <Button
          variant="contained"
          fullWidth
          disabled={!canSubmit}
          onClick={handleSubmit}
          theme={theme}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            py: 1.25,
          }}
        >
          {submitting
            ? "Adding..."
            : selectedEmails.length === 0
              ? "Add emails to list"
              : `Add ${selectedEmails.length} email${selectedEmails.length === 1 ? "" : "s"} to list`}
        </Button>
      ) : null}
    </Box>
  );
};

export default EmailList;
